import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ApiService,
  DialogService,
  EntityCacheProvider,
  FormatService,
  LmapService,
  NotificationService,
  SeverityThreshold,
  TestServerConfigService,
  TransformationService,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';

@Injectable({
  providedIn: 'root'
})
export class NetworkTestService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  exceedsIPRange: boolean;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  formatService: FormatService;
  lmapService: LmapService;
  serverConfigService: TestServerConfigService;
  private CODEC_URL = '/monitor/MonitorManager/v1/codecs';
  private monitorMaxLimitUrl = '/monitor/MonitorManager/v1/monitor/activeMonitorsCountAndLimit';
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    formatService: FormatService,
    serverConfigService: TestServerConfigService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly notifyService: NotificationService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.formatService = formatService;
    this.lmapService = lmapService;
    this.serverConfigService = serverConfigService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {}
  ): void {
    super.openTestSetup(setupId, setupConfig, defaultParamsById, onSuccess, onFail);
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
  }
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadCodecOptions(callbackFn: (any) => any): void {
    this.apiService
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get$<any>(this.CODEC_URL, {})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          callbackFn(response);
        },
        error => {
          callbackFn(error);
        },
        () => {}
      );
  }

  //eslint-disable-next-line
  getRequestObject(): any {
    const requestObject = {};
    requestObject['runType'] = 'on-going';
    requestObject['alias'] = this.output.testName;
    requestObject['metaData'] = [];
    if (this.editMode) {
      requestObject['destination'] = this.output.destination;
      requestObject['originator'] = this.output.originator;
      requestObject['monitorId'] = this.output.monitorId;
      requestObject['testType'] = this.output.testType;

      return requestObject;
    }
    requestObject['src_site_id'] = this.output.originator.site.id;
    requestObject['dst_site_id'] = this.output.destination.site.id;
    const originator = {
      location: {
        coordinate: {
          lat: this.output.originator.site.location.lat,
          lon: this.output.originator.site.location.lon
        },
        name: this.output.originator.id
      },
      status: this.output.originator.status,
      siteName: this.output.originator.site.label,
      country: this.output.originator.site.location.country
    };
    const destination = {
      agent: {
        location: {
          coordinate: {
            lat: this.output.destination.site.location.lat,
            lon: this.output.destination.site.location.lon
          },
          name: this.output.destination.id
        },
        status: this.output.destination.status,
        siteName: this.output.destination.site.label,
        country: this.output.destination.site.location.country
      }
    };
    const service = {
      alias: 'Network',
      destination: destination,
      tests: [
        {
          description: 'Monitor network connections between two sites',
          id: 'network-quality',
          label: 'Network'
        }
      ]
    };
    if (this.output.originator.itemType === 'device') {
      originator['alias'] = this.output.originator.name.label;
      originator['id'] = this.output.originator.id;
      originator['isCloudAgent'] = true;
    }
    if (this.output.destination.itemType === 'device') {
      destination['agent']['alias'] = this.output.destination.name.label;
      destination['agent']['id'] = this.output.destination.id;
      destination['agent']['isCloudAgent'] = true;
    }
    requestObject['service'] = service;
    requestObject['originator'] = originator;
    return requestObject;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getMonitorMaxLimit$(): any {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.get$<any>(this.monitorMaxLimitUrl, {});
  }
  //eslint-disable-next-line
  getThresholdRequestObject(): any {
    const thresholdObject = this.output.networkThreshold;
    const networkThresholdEntity = [];
    Object.keys(thresholdObject).forEach(key => {
      const kpiThreshold = thresholdObject[key].kpiThreshold;
      const unit = thresholdObject[key].unit;
      const thresholdValue =
        unit === 'ms'
          ? thresholdObject[key].thresholdValue * 1000
          : thresholdObject[key].thresholdValue;
      if (kpiThreshold) {
        const kpiObject = {
          kpiname: kpiThreshold.kpiname,
          dimensions: kpiThreshold.dimensions,
          expression: kpiThreshold.expression,
          severityThresholds: this.getSeverityThresholdObject(thresholdValue)
        };
        networkThresholdEntity.push(kpiObject);
      }
    });
    return networkThresholdEntity;
  }

  getSeverityThresholdObject(thresholdValue: number): Array<SeverityThreshold> {
    //assign with empty when no site defined for agent
    const siteId = this.output.siteId || '';
    const staticValues = siteId
      ? `${siteId},${this.output.monitorid}`
      : `9999,${this.output.monitorid}`;
    return [
      {
        staticValues: `${staticValues}`,
        severityThresholdPairs: [
          {
            severity: 'Critical',
            threshold: thresholdValue
          },
          {
            severity: 'Warning',
            threshold: thresholdValue
          }
        ]
      }
    ];
  }
}
